import AdminPanel from './AdminPanel';
import { useState, useEffect, useRef, useCallback } from 'react';
import { db, saveBooking, updateBooking } from './firebase';
import { collection, addDoc, onSnapshot, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { CONFIGS, calculatePriceForSlot, PENDING_VERIFICATION_MINUTES } from './config';
import { isAdminEntry } from './utils';
import './App.css';

const MAX_BANK_TRIES = 2;
const BANK_REVEAL_SECONDS = 5 * 60;
const FACILITIES_PREVIEW_COUNT = 6;

function App() {
  const [farmhouse, setFarmhouse] = useState(
    window.location.hash === '#silver' ? 'silver' : 'gold'
  );
  const config = CONFIGS[farmhouse];

  const [showAllFacilities, setShowAllFacilities] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      localStorage.setItem('darkMode', String(!prev));
      return !prev;
    });
  };

  // Straight to the Admin Dashboard on the dedicated subdomain or `#admin` —
  // skips the landing page and booking flow entirely. Same Firebase Auth
  // email/password gate as always (see AdminPanel.js); this only changes
  // which screen loads first, not who's allowed in.
  const [showAdmin, setShowAdmin] = useState(() => isAdminEntry());
  const [bookingType, setBookingType] = useState('family');
  const [checkInDate, setCheckInDate] = useState('');
  const [slotType, setSlotType] = useState('10hour-day');
  const [guestCount, setGuestCount] = useState(1);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoMessage, setPromoMessage] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [nicNo, setNicNo] = useState('');
  const [agreeAdvance, setAgreeAdvance] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [paymentErrors, setPaymentErrors] = useState({});
  const [showTerms, setShowTerms] = useState(false);
  // Public-safe shadow of `bookings` (date/unit/slotType/status only) — see
  // firestore.rules. This is all the customer-facing page ever needs for the
  // live availability check; the full `bookings` collection is admin-only.
  const [availability, setAvailability] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [holds, setHolds] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [showInquiry, setShowInquiry] = useState(false);
  const [currentInquiry, setCurrentInquiry] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [numberCopied, setNumberCopied] = useState(false);
  const [bankDetailsVisible, setBankDetailsVisible] = useState(false);
  const [bankTriesUsed, setBankTriesUsed] = useState(0);
  const [bankTimeLeft, setBankTimeLeft] = useState(0);
  const [myBooking, setMyBooking] = useState(null);
  const [avail, setAvail] = useState(null);
  const [atMax, setAtMax] = useState(false);
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const holdTimerRef    = useRef(null);
  const holdIntervalRef = useRef(null);

  const stopHold = useCallback(() => {
    clearTimeout(holdTimerRef.current);
    clearInterval(holdIntervalRef.current);
  }, []);

  const startHold = useCallback((direction, maxP) => {
    const step = () => {
      setGuestCount(prev => {
        const next = prev + direction;
        if (next < 1)    return 1;
        if (next > maxP) { setAtMax(true); setTimeout(() => setAtMax(false), 600); return prev; }
        setAtMax(false);
        return next;
      });
    };
    step(); // fire immediately on press
    holdTimerRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(step, 80);
    }, 350);
  }, []);

  useEffect(() => {
    return () => stopHold(); // cleanup on unmount
  }, [stopHold]);

  useEffect(() => {
    setHeadlineIdx(0);
    const t = setInterval(() => {
      setHeadlineIdx(i => (i + 1) % config.headlines.length);
    }, 3200);
    return () => clearInterval(t);
  }, [config.headlines.length, farmhouse]);

  // Bank details countdown — ticks down once per second while visible, then
  // auto-hides. The reveal itself is what consumes a try (see
  // handleShowBankDetails), not the timer running out.
  useEffect(() => {
    if (!bankDetailsVisible) return;
    if (bankTimeLeft <= 0) { setBankDetailsVisible(false); return; }
    const t = setTimeout(() => setBankTimeLeft(prev => prev - 1), 1000);
    return () => clearTimeout(t);
  }, [bankDetailsVisible, bankTimeLeft]);

  const handleShowBankDetails = () => {
    if (bankTriesUsed >= MAX_BANK_TRIES) return;
    setBankTriesUsed(prev => prev + 1);
    setBankTimeLeft(BANK_REVEAL_SECONDS);
    setBankDetailsVisible(true);
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'availability'),
      (snapshot) => setAvailability(snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }))),
      (err) => {
        console.error('availability listener error:', err);
        alert('⚠️ Could not load availability from Firebase.\n\nError: ' + err.message);
      }
    );
    return () => unsubscribe();
  }, []);

  // Auto-expiry sweep — there's no backend/scheduled job, so whichever
  // browser (customer or admin) happens to have the page open checks every
  // 20s for bookings whose payment window ran out and flips them to
  // 'expired'. Reads only the public `availability` collection (the full
  // `bookings` collection is admin-only now) and updates both docs by id via
  // updateBooking() — firestore.rules allows this exact narrow transition on
  // both collections, so it's safe even though it runs unauthenticated.
  useEffect(() => {
    const sweep = () => {
      const now = Date.now();
      availability
        .filter(a => a.status === 'pending_verification' && a.paymentDeadline && new Date(a.paymentDeadline).getTime() < now)
        .forEach(a => {
          if (!a.firestoreId) return;
          updateBooking(a.firestoreId, { status: 'expired' }).catch(err =>
            console.error('Failed to auto-expire booking', a.firestoreId, err)
          );
        });
    };
    const interval = setInterval(sweep, 20000);
    return () => clearInterval(interval);
  }, [availability]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'blockedDates'),
      (snapshot) => setBlockedDates(snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }))),
      (err) => console.error('blockedDates listener error:', err)
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'holds'),
      (snapshot) => setHolds(snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }))),
      (err) => console.error('holds listener error:', err)
    );
    return () => unsubscribe();
  }, []);

  // Same client-side auto-expiry pattern as the booking sweep above — timed
  // holds (30min/2hr/2days/7days) self-expire once their deadline passes.
  // Indefinite holds have no expiresAt and are never touched here; they stay
  // visible forever in the Active Holds list instead.
  useEffect(() => {
    const sweep = () => {
      const now = Date.now();
      holds
        .filter(h => h.status === 'active' && h.expiresAt && new Date(h.expiresAt).getTime() < now)
        .forEach(h => {
          updateDoc(doc(db, 'holds', h.firestoreId), { status: 'expired' }).catch(err =>
            console.error('Failed to auto-expire hold', h.firestoreId, err)
          );
        });
    };
    const interval = setInterval(sweep, 20000);
    return () => clearInterval(interval);
  }, [holds]);

  useEffect(() => {
    const q = query(collection(db, 'staffQuestions'), orderBy('askedAt', 'desc'));
    const unsubscribe = onSnapshot(q,
      (snapshot) => setQuestions(snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }))),
      (err) => console.error('staffQuestions listener error:', err)
    );
    return () => unsubscribe();
  }, []);

  const switchFarmhouse = (type) => {
    setFarmhouse(type);
    window.location.hash = '#' + type;
    setPromoApplied(false);
    setPromoCode('');
    setPromoError('');
    setPromoMessage('');
    setCheckInDate('');
    setSlotType('10hour-day');
    setAvail(null);
    setHeadlineIdx(0);
    setShowAllFacilities(false);
  };

  const validatePhone = (v) => /^03\d{9}$/.test(v);
  const validateName  = (v) => /^[a-zA-Z\s.'-]+$/.test(v);
  const validateNic    = (v) => /^\d{13}$/.test(v);

  // Slot availability for the currently selected date/unit — computed live
  // (not gated behind the "Check Availability" button) so the Slot Type
  // radios below can never be picked for a slot that's already taken.
  const getSlotAvailability = (dateStr) => {
    if (!dateStr) return null;
    const blocked = blockedDates.some(b => b.date === dateStr && (b.unit === 'both' || b.unit === config.id));
    // Expired/cancelled bookings never happened as far as availability is
    // concerned — that's the whole point of the 15-minute payment window
    // and of cancellation freeing the slot back up. Released/expired holds
    // are excluded the same way — only 'active' holds block anything.
    const relevant = availability.filter(a => a.date === dateStr && a.unit === config.id && a.status !== 'expired' && a.status !== 'cancelled');
    const activeHolds = holds.filter(h => h.date === dateStr && h.unit === config.id && h.status === 'active');
    const isSlotHeld = (slotType) => activeHolds.some(h => h.slotType === slotType || h.slotType === '22hour' || slotType === '22hour');
    const dayBooked   = blocked || relevant.some(a => a.slotType === '10hour-day'   || a.slotType === '10hour' || a.slotType === '22hour') || isSlotHeld('10hour-day');
    const nightBooked = blocked || relevant.some(a => a.slotType === '10hour-night' || a.slotType === '22hour') || isSlotHeld('10hour-night');
    return { dayBooked, nightBooked, blocked };
  };
  const slotAvailability = getSlotAvailability(checkInDate);

  const validateForm = () => {
    const e = {};
    if (!name.trim())          e.name  = 'Name is required';
    else if (!validateName(name)) e.name = "Letters, spaces, and . ' - only";
    if (!phone)                e.phone = 'Phone is required';
    else if (!validatePhone(phone)) e.phone = 'Must be 11 digits starting with 03';
    if (!nicNo)                e.nic   = 'CNIC is required';
    else if (!validateNic(nicNo)) e.nic = 'Must be exactly 13 digits';
    if (!checkInDate)          e.date  = 'Start date is required';
    else if (new Date(checkInDate) < new Date().setHours(0,0,0,0)) e.date = 'Cannot book past dates';
    if (slotAvailability) {
      const slotTaken =
        (slotType === '10hour-day'   && slotAvailability.dayBooked) ||
        (slotType === '10hour-night' && slotAvailability.nightBooked) ||
        (slotType === '22hour'       && (slotAvailability.dayBooked || slotAvailability.nightBooked));
      if (slotTaken) e.slot = 'This slot is no longer available for the selected date — please pick another.';
    }
    if (guestCount < 1 || guestCount > config.maxPersons) e.guests = `Guests must be between 1 and ${config.maxPersons}`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getSlotLabel = (v) => ({
    '10hour-day':   '10 Hours (8 AM – 6 PM)',
    '10hour-night': '10 Hours (8 PM – 6 AM)',
    '22hour':       '22 Hours (8 AM – 6 AM)',
  }[v] || v);

  const basePrice    = calculatePriceForSlot(config, checkInDate, slotType);
  const subtotal     = basePrice;
  const promoDiscount = promoApplied ? config.promoDiscount : 0;
  const totalPrice   = Math.max(0, subtotal - promoDiscount);
  const advanceDue   = Math.ceil(totalPrice * 0.5);
  const remainingDue = totalPrice - advanceDue;

  const handleApplyPromoCode = () => {
    if (promoApplied) { setPromoError('Promo already applied.'); return; }
    if (promoCode.trim().toUpperCase() === config.promoCode) {
      setPromoApplied(true);
      setPromoError('');
      setPromoMessage(`✅ Promo applied! You saved Rs. ${config.promoDiscount.toLocaleString()}.`);
    } else {
      setPromoError('Invalid promo code.');
      setPromoMessage('');
    }
  };

  const checkAvailability = () => {
    if (!checkInDate) return;
    setAvail(getSlotAvailability(checkInDate));
  };

  const handleSubmitInquiry = () => {
    if (!validateForm()) return;
    setCurrentInquiry({
      id: Date.now(),
      farmhouse: config.id,
      unit: config.unit,
      name, phone, nicNo, bookingType, guestCount,
      checkInDate,
      slotType, totalPrice,
      status: 'pending_payment',
      createdAt: new Date().toISOString(),
    });
    setPaymentErrors({});
    setShowTerms(false);
    setShowInquiry(true);
    setBankDetailsVisible(false);
    setBankTriesUsed(0);
    setBankTimeLeft(0);
  };

  const handleConfirmPayment = async () => {
    const pe = {};
    if (!agreeAdvance) pe.advanceAgreement = 'You must agree that advance payment is required.';
    if (!agreeTerms)   pe.terms = 'You must agree to the Terms & Conditions.';
    if (Object.keys(pe).length > 0) { setPaymentErrors(pe); return; }

    const finalizedBooking = {
      ...currentInquiry,
      // Not actually confirmed yet — staff still has to verify the advance
      // really landed. If nobody verifies within PENDING_VERIFICATION_MINUTES,
      // the auto-expiry sweep (see the effect below) flips this to 'expired'
      // and frees the slot back up.
      status: 'pending_verification',
      paymentStatus: 'Pending',
      paymentDeadline: new Date(Date.now() + PENDING_VERIFICATION_MINUTES * 60 * 1000).toISOString(),
      advance: advanceDue,
      balance: remainingDue,
      promoApplied,
      originalTotal: subtotal,
      discountedTotal: totalPrice,
      discountAmount: promoApplied ? config.promoDiscount : 0,
      createdAt: new Date().toISOString(),
    };

    setMyBooking(finalizedBooking);
    saveBooking(finalizedBooking).then(result => {
      setMyBooking(prev => (prev ? { ...prev, firestoreId: result.id } : prev));
    }).catch(err => {
      console.error('Firebase save failed:', err);
      alert('⚠️ Booking saved on this device but could NOT sync to Firebase.\n\nError: ' + err.message);
    });

    setShowInquiry(false);
    setShowConfirmation(true);
    setName(''); setPhone(''); setNicNo(''); setCheckInDate('');
    setGuestCount(1); setBookingType('family');
    setAgreeAdvance(false); setAgreeTerms(false);
    setPaymentErrors({}); setErrors({});
    setShowTerms(false); setPromoCode('');
    setPromoApplied(false); setPromoError(''); setPromoMessage('');
  };

  const handleCopyWhatsappNumber = async () => {
    try {
      await navigator.clipboard.writeText('0339-2959290');
      setNumberCopied(true);
      setTimeout(() => setNumberCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  const themeClass = farmhouse === 'silver' ? 'theme-silver' : 'theme-gold';

  if (showAdmin) {
    return (
      <AdminPanel
        blockedDates={blockedDates}
        questions={questions}
        holds={holds}
        onAddBlockedDate={async (blockedDate) => {
          await addDoc(collection(db, 'blockedDates'), blockedDate);
        }}
        onAddQuestion={async (questionText) => {
          await addDoc(collection(db, 'staffQuestions'), {
            question: questionText,
            askedAt: new Date().toISOString(),
            answer: null,
          });
        }}
        onAnswerQuestion={async (firestoreId, answer) => {
          await updateDoc(doc(db, 'staffQuestions', firestoreId), { answer, answeredAt: new Date().toISOString() });
        }}
        onBack={() => setShowAdmin(false)}
      />
    );
  }

  // ─── CHECKOUT PAGE ────────────────────────────────────────────────────────
  if (showInquiry && currentInquiry) {
    return (
      <div className={`page-shell ${themeClass} ${darkMode ? 'dark-mode' : ''}`}>
        <img src="/logo.png" alt="Logo" className="logo" />
        <button onClick={toggleDarkMode} className="theme-toggle-btn" aria-label="Toggle dark mode">{darkMode ? '☀️' : '🌙'}</button>
        <div className="payment-container">
          <div className="payment-box">
            <div className={`checkout-badge ${farmhouse === 'silver' ? 'badge-silver' : 'badge-gold'}`}>
              {farmhouse === 'silver' ? '🥈' : '🥇'} {config.unit}
            </div>
            <h2 className="checkout-title">🔥 Confirm Your Booking 🔥</h2>
            <p className="checkout-subtitle">{config.confirmMsg}</p>

            <div className="booking-summary">
              <h3>Booking Details</h3>
              <p><strong>Name:</strong> <span>{currentInquiry.name}</span></p>
              <p><strong>Phone:</strong> <span>{currentInquiry.phone}</span></p>
              <p><strong>CNIC:</strong> <span>{currentInquiry.nicNo}</span></p>
              <p><strong>Type:</strong> <span>{currentInquiry.bookingType === 'family' ? '👨‍👩‍👧‍👦 Family' : '👥 Friends'}</span></p>
              <p><strong>Guests:</strong> <span>{currentInquiry.guestCount} persons</span></p>
              <p><strong>Date:</strong> <span>{currentInquiry.checkInDate}</span></p>
              <p><strong>Slot:</strong> <span>{getSlotLabel(currentInquiry.slotType)}</span></p>
              <p><strong>Location:</strong> <span>📍 {config.location}</span></p>
            </div>

            <div className="price-breakdown">
              <div className="price-row"><span>Base Price:</span><span>Rs. {basePrice.toLocaleString()}</span></div>
              <div className="price-row"><span>Security Deposit (Refundable, paid at entry):</span></div>
              {promoApplied && <div className="price-row promo-row"><span>Promo Discount ({config.promoCode}):</span><span>− Rs. {config.promoDiscount.toLocaleString()}</span></div>}
              <div className="price-row"><span>Advance Due (50%):</span><span>Rs. {advanceDue.toLocaleString()}</span></div>
              <div className="price-row total"><span><strong>Total Payable:</strong></span><span><strong>Rs. {totalPrice.toLocaleString()}</strong></span></div>
              <div className="price-note">Remaining balance Rs. {remainingDue.toLocaleString()} payable on-site.</div>
            </div>

            <div className="promo-box">
              <h3>🎟️ Promo Code</h3>
              <p className="promo-banner">{config.promoLabel}</p>
              <div className="promo-input-row">
                <input type="text" value={promoCode} onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); setPromoMessage(''); }} placeholder="Enter promo code" className="promo-input" />
                <button type="button" className="promo-apply-btn" onClick={handleApplyPromoCode}>Apply</button>
              </div>
              {promoError && <p className="promo-error">{promoError}</p>}
              {promoMessage && <p className="promo-success">{promoMessage}</p>}
            </div>

            <div className="payment-methods">
              <h3>Payment Method</h3>
              <div className="bank-method-box">
                <div className="bank-method-header">🏦 Meezan Bank Transfer</div>

                {bankTriesUsed >= MAX_BANK_TRIES ? (
                  <div className="bank-exhausted">
                    <p><strong>Failed to make payment?</strong> Contact us on WhatsApp for help.</p>
                    <a href="http://wa.me/+923392959290" target="_blank" rel="noreferrer" className="btn-open-whatsapp">💬 Contact WhatsApp Support</a>
                  </div>
                ) : bankDetailsVisible ? (
                  <div className="bank-details-reveal">
                    <p className="bank-detail-line"><strong>FAIZAN RIAZ UDDIN</strong></p>
                    <p className="bank-detail-line">Meezan Bank – PIB Colony Branch, Karachi</p>
                    <p className="bank-detail-line">Account Number: <strong>99470108413692</strong></p>
                    <p className="bank-detail-line bank-iban-line">IBAN: <strong>PK57MEZN0099470108413692</strong></p>
                    <div className="bank-qr-wrap">
                      <img src="/Bankqr.jpg" alt="Meezan Bank payment QR code" className="bank-qr-img" />
                      <p className="bank-qr-caption">Or scan with your banking app</p>
                    </div>
                    <p className="bank-timer">⏳ Hiding in {Math.floor(bankTimeLeft / 60)}:{String(bankTimeLeft % 60).padStart(2, '0')}</p>
                  </div>
                ) : (
                  <button type="button" className="btn-show-bank" onClick={handleShowBankDetails}>
                    👁️ Click to Show Account Details ({MAX_BANK_TRIES - bankTriesUsed} view{MAX_BANK_TRIES - bankTriesUsed === 1 ? '' : 's'} left)
                  </button>
                )}
              </div>
            </div>

            <div className="payment-terms-section">
              <label className="checkbox-agree">
                <input type="checkbox" checked={agreeAdvance} onChange={(e) => setAgreeAdvance(e.target.checked)} />
                I understand that without advance payment, Riaz Farmhouse will not book my selected slot.
              </label>
              {paymentErrors.advanceAgreement && <span className="error-text">{paymentErrors.advanceAgreement}</span>}
              <label className="checkbox-agree" style={{marginTop:'10px'}}>
                <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} />
                I have read and agree to the <button type="button" className="terms-link" onClick={() => setShowTerms(!showTerms)}>Terms & Conditions</button>.
              </label>
              {paymentErrors.terms && <span className="error-text">{paymentErrors.terms}</span>}
              {showTerms && <TermsContent />}
            </div>

            <p className="payment-note payment-alert">
              <strong>After payment, send the screenshot to WhatsApp at 0339-2959290 for verification. Without verification your booking is NOT confirmed.</strong>
            </p>

            <button onClick={handleConfirmPayment} className="btn-confirm">✅ I've Made Payment — Confirm Booking</button>
            <button onClick={() => setShowInquiry(false)} className="btn-cancel">← Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── BOOKING CONFIRMED SCREEN ─────────────────────────────────────────────
  if (showConfirmation) {
    return (
      <div className={`page-shell ${themeClass} ${darkMode ? 'dark-mode' : ''}`}>
        <img src="/logo.png" alt="Logo" className="logo" />
        <button onClick={toggleDarkMode} className="theme-toggle-btn" aria-label="Toggle dark mode">{darkMode ? '☀️' : '🌙'}</button>
        <div className="confirmation-container">
          <div className="confirmation-box">
            <svg className="confirmation-check" viewBox="0 0 100 100">
              <circle className="confirmation-check-circle" cx="50" cy="50" r="45" fill="none" strokeWidth="6" />
              <path className="confirmation-check-mark" fill="none" strokeWidth="7" d="M28 52 L43 66 L74 34" />
            </svg>
            <h2 className="confirmation-title">Booking Received!</h2>
            <div className="confirmation-highlight">
              <strong>Send your payment screenshot to WhatsApp at 0339-2959290 for verification.</strong>
              <p>We're holding your slot for {PENDING_VERIFICATION_MINUTES} minutes while our team verifies the payment — if it isn't verified in time, the slot is released automatically.</p>
            </div>
            <div className="confirmation-actions">
              <button type="button" className="btn-copy-number" onClick={handleCopyWhatsappNumber}>
                {numberCopied ? '✅ Copied!' : '📋 Copy Number'}
              </button>
              <a href="http://wa.me/+923392959290" target="_blank" rel="noreferrer" className="btn-open-whatsapp">
                💬 Open WhatsApp
              </a>
            </div>
            <button type="button" className="btn-continue" onClick={() => setShowConfirmation(false)}>Continue →</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── MAIN BOOKING PAGE ────────────────────────────────────────────────────
  return (
    <div className={`page-shell ${themeClass} ${darkMode ? 'dark-mode' : ''}`}>
      <img src="/logo.png" alt="Riaz Farmhouse Logo" className="logo" />
      <button onClick={toggleDarkMode} className="theme-toggle-btn" aria-label="Toggle dark mode">{darkMode ? '☀️' : '🌙'}</button>

      {/* Farmhouse Switcher */}
      <div className="farmhouse-switcher">
        <button
          className={`switch-btn ${farmhouse === 'gold' ? 'switch-active-gold' : ''}`}
          onClick={() => switchFarmhouse('gold')}
        >
          🥇 Gold Farmhouse
        </button>
        <button
          className={`switch-btn ${farmhouse === 'silver' ? 'switch-active-silver' : ''}`}
          onClick={() => switchFarmhouse('silver')}
        >
          🥈 Silver Farmhouse
        </button>
      </div>

      <div className="booking-container">
        <div className={`headline-banner ${farmhouse === 'silver' ? 'headline-silver' : 'headline-gold'}`}>
          <span key={headlineIdx} className="headline-text">
            {config.headlines[headlineIdx]}
          </span>
        </div>

        <header className="header">
          <h1>{config.name}</h1>
          <p className={`unit-name ${farmhouse === 'silver' ? 'unit-silver' : ''}`}>{config.unit}</p>
          <p className="tagline">{config.tagline}</p>
          {config.location && <p className="tagline" style={{fontSize:'12px',marginTop:'3px'}}>📍 {config.location}</p>}
          <button onClick={() => setShowAdmin(true)} className="admin-btn">⚙️ Admin</button>
        </header>

        {/* Facilities — shown for both farmhouses, collapsed to a short preview
            on load so the page doesn't force a long scroll before the actual
            booking form; "Show All" reveals the rest. */}
        {config.facilities && (() => {
          const visibleFacilities = showAllFacilities ? config.facilities : config.facilities.slice(0, FACILITIES_PREVIEW_COUNT);
          const remaining = config.facilities.length - FACILITIES_PREVIEW_COUNT;
          return (
            <div className="facilities-section">
              <h3>
                {farmhouse === 'gold' ? '🥇 RIAZ GOLD FARMHOUSE' : '🥈 RIAZ SILVER FARMHOUSE'} — FACILITIES
              </h3>
              <div className="facilities-grid">
                {visibleFacilities.map((f, i) => (
                  <div key={i} className="facility-item">
                    <span className="fac-icon">{f.icon}</span>
                    <div className="fac-text">
                      <strong className="fac-title">{f.title}</strong>
                      {f.detail && <span className="fac-detail">{f.detail}</span>}
                    </div>
                  </div>
                ))}
              </div>
              {remaining > 0 && (
                <button type="button" className="btn-toggle-facilities" onClick={() => setShowAllFacilities(v => !v)}>
                  {showAllFacilities ? '▲ Show Less' : `▼ Show All Facilities (${remaining} more)`}
                </button>
              )}
            </div>
          );
        })()}

        <div className="booking-form">
          <div className="form-section">
            <h3>👥 Who Are You?</h3>
            <div className="form-group">
              <label>Booking Type</label>
              <div className="radio-group">
                <label className="radio-option"><input type="radio" value="family" checked={bookingType === 'family'} onChange={(e) => setBookingType(e.target.value)} />👨‍👩‍👧‍👦 Family Only</label>
                <label className="radio-option"><input type="radio" value="friends" checked={bookingType === 'friends'} onChange={(e) => setBookingType(e.target.value)} />👥 Friends/Group</label>
              </div>
              <p className="warning">⚠️ Illegal activities are not allowed</p>
            </div>
          </div>

          <div className="form-section">
            <h3>📋 Your Information</h3>
            <div className="form-group">
              <label>Full Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z\s.'-]/g, ''))} placeholder="Enter your full name" className={errors.name ? 'input-error' : ''} />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="03001234567 (11 digits)" maxLength="11" className={errors.phone ? 'input-error' : ''} />
              {errors.phone && <span className="error-text">{errors.phone}</span>}
            </div>
            <div className="form-group">
              <label>CNIC / Smart Card / NIC / Shanakhti Card / National Identity Card *</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={nicNo}
                onChange={(e) => setNicNo(e.target.value.replace(/\D/g, '').slice(0, 13))}
                placeholder="e.g. 42100xxxxxxx0 (13 digits, no dashes)"
                maxLength="13"
                className={errors.nic ? 'input-error' : ''}
              />
              {errors.nic && <span className="error-text">{errors.nic}</span>}
              <p className="field-hint">Enter your 13-digit CNIC number without dashes.</p>
            </div>
            <div className="form-group">
              <label>Number of Guests *</label>
              <div className="guest-step-grid" style={{ gridTemplateColumns: `repeat(${config.personSteps.length}, 1fr)` }}>
                {config.personSteps.map(n => (
                  <button
                    key={n}
                    type="button"
                    className={`guest-step-btn ${guestCount === n ? 'guest-step-active' : ''}`}
                    onClick={() => setGuestCount(n)}
                  >{n}<span className="guest-step-sub">persons</span></button>
                ))}
              </div>
              <div className="guest-fine-row">
                <button
                  type="button"
                  className="guest-fine-btn"
                  onMouseDown={() => startHold(-1, config.maxPersons)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={(e) => { e.preventDefault(); startHold(-1, config.maxPersons); }}
                  onTouchEnd={stopHold}
                >−</button>
                <span className={`guest-fine-display ${atMax ? 'guest-at-max' : ''}`}>
                  {guestCount}
                  <span className="guest-fine-label">{atMax ? '🚫 MAX REACHED' : 'persons'}</span>
                </span>
                <button
                  type="button"
                  className={`guest-fine-btn guest-fine-plus ${guestCount >= config.maxPersons ? 'guest-fine-disabled' : ''}`}
                  disabled={guestCount >= config.maxPersons}
                  onMouseDown={() => startHold(1, config.maxPersons)}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={(e) => { e.preventDefault(); startHold(1, config.maxPersons); }}
                  onTouchEnd={stopHold}
                >+</button>
              </div>
              {errors.guests && <span className="error-text">{errors.guests}</span>}
              <p className="guest-note">
                <strong>⚠️ MAX CAPACITY: {config.maxPersonsLabel}</strong> — Kids excluded from count.<br/>
                <span className="guest-warning">🚨 Bringing MORE guests than the maximum? <strong>EXTRA CHARGES WILL APPLY</strong> — confirm with us before booking.</span>
              </p>
            </div>
          </div>

          <div className="form-section">
            <h3>📅 Select Your Slot</h3>
            <div className="form-group">
              <label>Start Date *</label>
              <input type="date" value={checkInDate} onChange={(e) => { setCheckInDate(e.target.value); setAvail(null); setSlotType('10hour-day'); }} className={errors.date ? 'input-error' : ''} />
              {errors.date && <span className="error-text">{errors.date}</span>}
            </div>

            {/* ── Availability Check ─────────────────────────── */}
            {checkInDate && (
              <div className="avail-check-wrap">
                {!avail ? (
                  <button type="button" className="btn-check-avail" onClick={checkAvailability}>
                    🔍 Check Availability for This Date &amp; Slot
                  </button>
                ) : (() => {
                  const { dayBooked, nightBooked, blocked } = avail;
                  const fullyBooked = dayBooked && nightBooked;

                  if (fullyBooked) return (
                    <div className="avail-result avail-full">
                      <div className="avail-icon">😔</div>
                      <div className="avail-msg-block">
                        <strong className="avail-title">{blocked ? 'NOT AVAILABLE' : 'FULLY BOOKED'} — {checkInDate}</strong>
                        <p className="avail-sub">
                          {blocked
                            ? <>This date is unavailable on <em>{config.unit}</em>.</>
                            : <>The Day Slot, Night Slot, and 22-Hour Full Slot are all taken for this date on <em>{config.unit}</em>.</>}
                        </p>
                        <p className="avail-action-hint">Please pick a different date above.</p>
                      </div>
                      <button className="avail-recheck" onClick={() => { setCheckInDate(''); setAvail(null); }}>← Change Date</button>
                    </div>
                  );

                  if (dayBooked) return (
                    <div className="avail-result avail-partial">
                      <div className="avail-slot-row">
                        <div className="avail-slot-card avail-taken">
                          <span className="avail-slot-icon">☀️</span>
                          <strong>DAY SLOT</strong>
                          <span className="avail-slot-time">8 AM – 6 PM</span>
                          <span className="avail-slot-badge badge-taken">BOOKED</span>
                        </div>
                        <div className="avail-slot-card avail-open">
                          <span className="avail-slot-icon">🌙</span>
                          <strong>NIGHT SLOT</strong>
                          <span className="avail-slot-time">8 PM – 6 AM</span>
                          <span className="avail-slot-badge badge-open">AVAILABLE ✓</span>
                        </div>
                      </div>
                      <p className="avail-sorry">😔 <strong>Sorry! The Day Slot is already booked</strong> on {checkInDate}. The 22-Hour Full Slot is also unavailable, since it needs the Day Slot too.</p>
                      <button className="avail-switch-btn" onClick={() => setSlotType('10hour-night')}>🌙 Book Night Slot Instead — 8 PM to 6 AM</button>
                      <button className="avail-recheck" onClick={() => setAvail(null)}>← Check Again</button>
                    </div>
                  );

                  if (nightBooked) return (
                    <div className="avail-result avail-partial">
                      <div className="avail-slot-row">
                        <div className="avail-slot-card avail-open">
                          <span className="avail-slot-icon">☀️</span>
                          <strong>DAY SLOT</strong>
                          <span className="avail-slot-time">8 AM – 6 PM</span>
                          <span className="avail-slot-badge badge-open">AVAILABLE ✓</span>
                        </div>
                        <div className="avail-slot-card avail-taken">
                          <span className="avail-slot-icon">🌙</span>
                          <strong>NIGHT SLOT</strong>
                          <span className="avail-slot-time">8 PM – 6 AM</span>
                          <span className="avail-slot-badge badge-taken">BOOKED</span>
                        </div>
                      </div>
                      <p className="avail-sorry">😔 <strong>Sorry! The Night Slot is already booked</strong> on {checkInDate}. The 22-Hour Full Slot is also unavailable, since it needs the Night Slot too.</p>
                      <button className="avail-switch-btn" onClick={() => setSlotType('10hour-day')}>☀️ Book Day Slot Instead — 8 AM to 6 PM</button>
                      <button className="avail-recheck" onClick={() => setAvail(null)}>← Check Again</button>
                    </div>
                  );

                  return (
                    <div className="avail-result avail-all-clear">
                      <div className="avail-icon">🎉</div>
                      <div className="avail-msg-block">
                        <strong className="avail-title">GREAT NEWS — {checkInDate} IS OPEN!</strong>
                        <p className="avail-sub">The Day Slot, Night Slot, and 22-Hour Full Slot are all available on <em>{config.unit}</em>. Pick your preferred slot below.</p>
                      </div>
                      <div className="avail-slot-row">
                        <div className="avail-slot-card avail-open" onClick={() => setSlotType('10hour-day')} style={{cursor:'pointer'}}>
                          <span className="avail-slot-icon">☀️</span>
                          <strong>DAY SLOT</strong>
                          <span className="avail-slot-time">8 AM – 6 PM</span>
                          <span className="avail-slot-badge badge-open">TAP TO SELECT</span>
                        </div>
                        <div className="avail-slot-card avail-open" onClick={() => setSlotType('10hour-night')} style={{cursor:'pointer'}}>
                          <span className="avail-slot-icon">🌙</span>
                          <strong>NIGHT SLOT</strong>
                          <span className="avail-slot-time">8 PM – 6 AM</span>
                          <span className="avail-slot-badge badge-open">TAP TO SELECT</span>
                        </div>
                        <div className="avail-slot-card avail-open" onClick={() => setSlotType('22hour')} style={{cursor:'pointer'}}>
                          <span className="avail-slot-icon">⏱️</span>
                          <strong>22 HOUR SLOT</strong>
                          <span className="avail-slot-time">8 AM – 6 AM</span>
                          <span className="avail-slot-badge badge-open">TAP TO SELECT</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="form-group">
              <label>Slot Type *</label>
              <div className="slot-options">
                {[
                  { value: '10hour-day',   label: '10 Hour — Day Slot',   sub: '8 AM – 6 PM' },
                  { value: '10hour-night', label: '10 Hour — Night Slot', sub: '8 PM – 6 AM' },
                  { value: '22hour',       label: '22 Hour — Full Slot',  sub: '8 AM – 6 AM next day' },
                ].map(s => {
                  const isTaken = slotAvailability && (
                    (s.value === '10hour-day'   && slotAvailability.dayBooked) ||
                    (s.value === '10hour-night' && slotAvailability.nightBooked) ||
                    (s.value === '22hour'       && (slotAvailability.dayBooked || slotAvailability.nightBooked))
                  );
                  return (
                    <label key={s.value} className={`slot-card ${slotType === s.value ? 'slot-selected' : ''} ${isTaken ? 'slot-disabled' : ''}`}>
                      <input type="radio" value={s.value} checked={slotType === s.value} disabled={isTaken} onChange={() => setSlotType(s.value)} />
                      <div className="slot-info">
                        <strong>{s.label}</strong>
                        <p>{s.sub}</p>
                        {isTaken
                          ? <p className="slot-taken-badge">🚫 Already Booked</p>
                          : checkInDate && (
                            <p className="slot-price">
                              Rs. {calculatePriceForSlot(config, checkInDate, s.value).toLocaleString()}
                            </p>
                          )}
                      </div>
                    </label>
                  );
                })}
              </div>
              {errors.slot && <span className="error-text">{errors.slot}</span>}
              <div className="custom-booking-note">
                <strong>⏰ Need more than 22 hours?</strong> Call us at <strong>0300-2200883</strong> for a custom multi-day booking inquiry.
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>💰 Price & Payment</h3>
            <div className="price-display">
              <div className="price-item"><span>Base Price:</span><span className="price">Rs. {basePrice.toLocaleString()}</span></div>
              <div className="price-item"><span>Security Deposit (Refundable, paid at entry):</span></div>
              <div className="price-item"><span>Advance Due (50%):</span><span className="price">Rs. {advanceDue.toLocaleString()}</span></div>
              <div className="price-item total"><span><strong>Total Payable:</strong></span><span className="price-total"><strong>Rs. {totalPrice.toLocaleString()}</strong></span></div>
            </div>
            <p className="price-note">Remaining Rs. {remainingDue.toLocaleString()} payable on-site.</p>
          </div>

          <div className="form-section terms-section">
            <h3>⚖️ Terms & Conditions</h3>
            <div className="terms-box">
              <p><strong>IMPORTANT — HOUSE RULES:</strong></p>
              <ul>
                <li>🚫 <strong>No Alcohol</strong> — Strictly prohibited</li>
                <li>🚫 <strong>No Drug Use</strong> — Any illegal substances forbidden</li>
                <li>🚫 <strong>No Betel/Paan/Gutka/Mawa</strong></li>
                <li>🚫 <strong>No Illegal Activities</strong> — Serious legal action</li>
                <li>🚨 <strong>Maximum {config.maxPersonsLabel}</strong> — extra charges apply beyond limit</li>
              </ul>
              <p className="warning-text"><strong>⚠️ WARNING:</strong> Any violation results in immediate eviction and forfeiture of security deposit. CCTV surveillance throughout the property.</p>
            </div>
          </div>

          <button onClick={handleSubmitInquiry} className="btn-submit">→ Request Booking (Advance Payment Required)</button>

          <div className="help-box">
            <p><strong>Need help? Chat on WhatsApp for quick booking assistance.</strong></p>
            <a href="http://wa.me/+923392959290" target="_blank" rel="noreferrer" className="whatsapp-help-button">
              <span className="whatsapp-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M20.52 3.48C18.39 1.35 15.38 0 12 0 5.37 0 0 5.37 0 12c0 2.1.55 4.15 1.59 5.95L0 24l6.27-1.64A11.92 11.92 0 0012 24c6.63 0 12-5.37 12-12 0-3.38-1.35-6.39-3.48-8.52z" fill="#25D366"/><path d="M17.55 14.63c-.29-.14-1.73-.86-2-.96-.27-.1-.47-.14-.67.14-.19.29-.74.96-.91 1.16-.17.19-.33.21-.62.07-.29-.14-1.22-.45-2.33-1.44-.86-.76-1.44-1.7-1.61-1.99-.17-.29-.02-.44.12-.58.12-.12.27-.32.4-.48.13-.16.17-.28.26-.46.09-.19.05-.35-.02-.49-.07-.14-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.19 0-.5.07-.77.35-.27.28-1.03 1.01-1.03 2.47s1.06 2.86 1.21 3.06c.14.19 2.09 3.18 5.07 4.46.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.09 1.73-.7 1.98-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.19-.57-.33z" fill="#FFF"/></svg>
              </span>
              Contact WhatsApp Support
            </a>
          </div>
        </div>

        {/* Your Booking — local to this browser tab only, never fetched from
            a shared collection, so it's gone on refresh and other visitors
            never see it. */}
        {myBooking && myBooking.farmhouse === farmhouse && (
          <div className="bookings-list">
            <h3>Your Booking</h3>
            {(() => {
              // Merge in the live *status* from the public `availability` doc
              // (by firestoreId) so this reflects admin verification or the
              // 15-min auto-expiry without a refresh — everything else here
              // (name/price/etc.) is what the customer themselves submitted,
              // cached locally, since the full `bookings` doc isn't something
              // an unauthenticated visitor can re-fetch anymore.
              const liveAvail = availability.find(a => a.firestoreId === myBooking.firestoreId);
              const booking = liveAvail ? { ...myBooking, status: liveAvail.status } : myBooking;
              const isPaid = booking.paymentStatus === 'Completed' || Number(booking.balance) === 0;
              const statusInfo =
                booking.status === 'expired' ? { cls: 'status-expired', label: '⏰ Expired — Slot Released' } :
                booking.status === 'pending_verification' ? { cls: 'status-pending', label: '⏳ Awaiting Verification' } :
                { cls: 'confirmed', label: '✅ Confirmed' };
              return (
                <div className={`booking-card ${isPaid ? 'booking-paid' : 'booking-pending'}`}>
                  <div className="booking-header">
                    <strong>{booking.name}</strong>
                    <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                      {booking.status === 'confirmed' && (isPaid
                        ? <span className="pay-badge pay-done">✅ Fully Paid</span>
                        : <span className="pay-badge pay-pending">⏳ Balance Rs. {Number(booking.balance || 0).toLocaleString()}</span>
                      )}
                      <span className={`status ${statusInfo.cls}`}>{statusInfo.label}</span>
                    </div>
                  </div>
                  <p>👥 {booking.guestCount} guests</p>
                  <p>📅 {booking.checkInDate} | {getSlotLabel(booking.slotType)}</p>
                  {booking.promoApplied
                    ? <p className="booking-price-row"><span className="price-strike">Rs. {Number(booking.originalTotal).toLocaleString()}</span><span className="price-highlight">Rs. {Number(booking.discountedTotal).toLocaleString()}</span></p>
                    : <p>💰 Rs. {Number(booking.totalPrice).toLocaleString()}</p>
                  }
                  {booking.status === 'expired' && (
                    <p className="booking-expired-note">This request expired before payment was verified. Please contact WhatsApp support or submit a new booking if you'd still like this slot.</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function TermsContent() {
  return (
    <div className="full-terms">
      <h3>Riaz Farmhouse – Terms & Conditions & House Rules</h3>
      <p><strong>Effective Immediately | Zero Tolerance Policy</strong></p>
      <h4>1. Legal Compliance & General Conduct</h4>
      <ul>
        <li><strong>100% illegal activity is banned.</strong> This includes drugs, gambling, theft, fighting, or any act prohibited by Pakistan law.</li>
        <li><strong>No alcohol permitted</strong> anywhere on the property. Possession = immediate eviction.</li>
        <li><strong>No immoral or inappropriate activities.</strong></li>
      </ul>
      <h4>2. Security & Surveillance</h4>
      <ul>
        <li><strong>24/7 CCTV surveillance</strong> covering all areas.</li>
        <li>Dua Greens Security has full authority to monitor and intervene at any time.</li>
      </ul>
      <h4>3. Prohibited Items & Actions</h4>
      <ul>
        <li><strong>No weapons of any kind.</strong> Gun firing is banned and will be reported to authorities.</li>
        <li><strong>No fireworks, crackers, or explosives.</strong></li>
        <li><strong>No loud music after 10:00 PM.</strong></li>
        <li><strong>No damage to property.</strong> You are financially liable for any damage.</li>
      </ul>
      <h4>4. Enforcement & Penalties</h4>
      <ul>
        <li><strong>Instant eviction</strong> with no refund for any rule violation.</li>
        <li><strong>Forfeiture of security deposit</strong> for any violation.</li>
        <li><strong>Legal action</strong> for illegal acts — Police/Rangers will be called.</li>
        <li><strong>Permanent blacklist</strong> from Riaz Farmhouse properties.</li>
      </ul>
      <h4>5. Guest Responsibility</h4>
      <ul>
        <li>The booking person is <strong>personally responsible</strong> for all guests.</li>
        <li>CNIC copies of all adults required before check-in. No ID, no entry.</li>
      </ul>
      <p><em>By paying the advance or entering the gate, you confirm you have read, understood, and agree to all terms above.</em></p>
    </div>
  );
}

export default App;
